import HttpClient from "api/index";
import {AxiosResponse} from "axios";
import {settleImportUrls} from "helpers/importErrorReason";
import {
	backgroundTaskApiPaginationOptions,
	backgroundTaskApiParameterOptions,
	TBackgroundTaskCreate,
} from "./types";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type TTaskBody = Record<string, any>;

/**
 * A task body with every url in its settled status (helpers/importErrorReason): a url whose
 * error_reason arrived before its status event reads as the error it is, so every count, toast and
 * report downstream treats it as finished without each of them knowing about error_reason.
 */
const settleTaskUrls = (task: TTaskBody): TTaskBody => {
	if (!task || typeof task !== "object") {
		return task;
	}

	const settled = {...task};

	if (Array.isArray(task.linkedin_candidate_urls)) {
		settled.linkedin_candidate_urls = settleImportUrls(task.linkedin_candidate_urls);
	}

	if (Array.isArray(task.linkedin_company_urls)) {
		settled.linkedin_company_urls = settleImportUrls(task.linkedin_company_urls);
	}

	return settled;
};

// doGet/doPost hand back the error response instead of throwing, so data may be an error body.
const withSettledTask = (response: AxiosResponse) =>
	response?.data ? {...response, data: settleTaskUrls(response.data)} : response;

const withSettledTasks = (response: AxiosResponse) =>
	Array.isArray(response?.data?.results)
		? {
				...response,
				data: {...response.data, results: response.data.results.map(settleTaskUrls)},
		  }
		: response;

class BackgroundTaskAPI extends HttpClient {
	async createBackgroundTaskRequest(formBody: TBackgroundTaskCreate, config = {}) {
		return withSettledTask(await this.doPost(`/background-tasks`, formBody, config));
	}

	async getAllBackgroundTaskRequests(
		parameterOptions: backgroundTaskApiParameterOptions,
		paginationOptions: backgroundTaskApiPaginationOptions,
		config: object,
	) {
		const {projectId, status, backgroundTaskType} = parameterOptions;
		const {page, perPage} = paginationOptions;

		// Base query with mandatory parameters
		let filterQuery = `?page=${page}&per_page=${perPage}&status=${status}&background_task_type=${backgroundTaskType}`;

		// Conditionally add the optional project_id parameter
		if (projectId !== undefined && projectId !== null) {
			filterQuery += `&project_id=${projectId}`;
		}

		return this.doGet(`/background-tasks${filterQuery}`, config);
	}

	async getBackgroundTasksById(id: number, config = {}) {
		return withSettledTask(await this.doGet(`/background-tasks/${id}`, config));
	}

	async getBatchBackgroundTasks(background_task_ids: number[], config = {}) {
		return withSettledTasks(
			await this.doPost(`/background-tasks/batch`, {background_task_ids}, config),
		);
	}
}

const mapXBackgroundTaskAPI = new BackgroundTaskAPI();

export default mapXBackgroundTaskAPI;
